import { Metadata } from 'nice-grpc-web';
import { v4 as uuidv4 } from 'uuid';
import { User } from '../state/reducers/authSlice';

import { FindAllOrderSnapshotsByOrderId } from '@/compiled_proto/com/celertech/marketmerchant/api/order/UpstreamOrderProto';
import { ContingencyType } from '@/compiled_proto/com/celertech/orderrouting/api/enums/ContingencyTypeProto';
import { ProductType } from '@/compiled_proto/com/celertech/orderrouting/api/enums/ProductTypeProto';
import {
    findSnapshotsFromAllOrders,
    parseBlotterOrder,
    parseLuceraBlotterOrder,
    parseOrderHistory
} from '@/helpers/blotterHelper';
import { orderServiceClient } from '@/helpers/environmentHelper';
import { getUniversalInstrument } from '@/utils/hooks/useInstrument';
import { HandlingInstruction } from '../compiled_proto/com/celertech/marketdata/api/enums/HandlingInstructionProto';
import { AccountType } from '../compiled_proto/com/celertech/orderrouting/api/enums/AccountTypeProto';
import { PegPriceType } from '../compiled_proto/com/celertech/orderrouting/api/enums/PegPriceTypeProto';
import { HistoricOrderSearchRequest } from '../compiled_proto/com/celertech/orderrouting/api/notification/OrderServiceProto';
import { KeyValue } from '../compiled_proto/com/celertech/orderrouting/api/order/OrderMetadata';
import {
    AmendOrderRequest,
    AmendStrategyOrderRequest,
    CancelOrderRequest,
    CreateFxListOrder,
    CreateFxOrderRequest,
    CreateFxStrategyLegOrder
} from '../compiled_proto/com/celertech/orderrouting/api/order/UpstreamOrderProto';
import { OrderType } from '../compiled_proto/com/celertech/positionmanager/api/enums/OrderTypeProto';
import { Side } from '../compiled_proto/com/celertech/positionmanager/api/enums/SideProto';
import { TimeInForceType } from '../compiled_proto/com/celertech/positionmanager/api/enums/TimeInForceTypeProto';
import { getTradingDayStart } from '../helpers/dateHelper';
import { BlotterItem, LuceraBlotterItem, OrderHistory } from '../model/blotters';
import { logToServer } from './LogService';
import { createLumeFxRestClient } from './LumeFxService';

const exchangeCode = window.config.integration.celertech.exchangeCode || 'XCEL';

interface MarketOrderArguments {
    spotPrice?: number;
    securityId: string;
    side: Side;
    quantity: number;
    currencyOut: string;
    timeInForce: TimeInForceType;
}

export async function submitMarketOrder(
    args: MarketOrderArguments,
    credentials: User,
    settlementDate?: string,
    currentAccount?: string
) {
    const config = getUniversalInstrument(args.securityId);

    const marketExchangeCode = exchangeCode;
    let marketSettlementType = 'SP';
    let marketProductType = ProductType.SPOT;

    if (['Index', 'Commodity'].includes(config?.type) && config?.product_type !== 'FX') {
        marketSettlementType = 'TOM';
        marketProductType = ProductType.CFD;
    }

    // Market order
    const orderRequestMarket = CreateFxOrderRequest.fromPartial({
        clientRequestId: uuidv4(),
        clientOrderId: uuidv4(),
        exchangeIdSource: 'MIC',
        exchange: marketExchangeCode,
        securityIdSource: 'EXCHANGE',
        securityCode: args.securityId,
        securityId: args.securityId,
        accountType: AccountType.CLIENT,
        account: currentAccount || credentials.accounts[0].code,
        side: args.side,
        qty: args.quantity,
        currency: args.currencyOut, // does quantity relate to base or quote
        orderType: OrderType.MARKET,
        timeInForce: args.timeInForce,
        handlingInstruction: HandlingInstruction.AUTOMATED_NO_BROKER,
        // quoteId: quoteId,
        productType: marketProductType,
        leg: [
            CreateFxStrategyLegOrder.fromPartial({
                legForwardPts: 0,
                legValueDate: settlementDate,
                qty: args.quantity,
                settlementType: marketSettlementType,
                side: args.side,
                spotPrice: args.spotPrice,
                underlyingCode: args.securityId,
                underlyingSecurityId: args.securityId
            })
        ], // See ts-proto useOptionals
        checkpointTimestamp: [],
        orderMetadata: [{ key: 'SLIPPAGE', value: '0' } as KeyValue],
        pegPriceType: PegPriceType.LAST_PEG
    });

    const orderSubmissionResponse = await orderServiceClient.unary.createFxOrderRequest(orderRequestMarket, {
        metadata: Metadata({
            'authorization-token': credentials.authToken
        })
    });

    logToServer(
        'info',
        JSON.stringify({
            message: `User [${credentials.username}] has requested a Market Order for Account [${
                currentAccount || credentials.accounts[0].code
            }].`,
            requestBody: orderRequestMarket,
            responseBody: orderSubmissionResponse
        })
    );

    // console.log('Order Submission Response', { orderRequestMarket, orderSubmissionResponse });
    return orderSubmissionResponse;
}

interface LimitOrderArguments extends MarketOrderArguments {
    limitPrice: number;
    slippage: string;
}

export async function submitLimitOrder(
    args: LimitOrderArguments,
    credentials: User,
    settlementDate?: string,
    currentAccount?: string
) {
    const config = getUniversalInstrument(args.securityId);

    const marketExchangeCode = exchangeCode;
    let marketSettlementType = 'SP';
    let marketProductType = ProductType.SPOT;

    if (['Index', 'Commodity'].includes(config?.type) && config?.product_type !== 'FX') {
        marketSettlementType = 'TOM';
        marketProductType = ProductType.CFD;
    }

    const slippage = args.slippage === 'NaN' ? '0' : args.slippage;
    // Limit order
    const orderRequestLimit = CreateFxOrderRequest.fromPartial({
        clientRequestId: uuidv4(),
        clientOrderId: uuidv4(),
        exchangeIdSource: 'MIC',
        exchange: marketExchangeCode,
        securityIdSource: 'EXCHANGE',
        securityCode: args.securityId,
        securityId: args.securityId,
        accountType: AccountType.CLIENT,
        account: currentAccount || credentials.accounts[0].code,
        side: args.side,
        qty: args.quantity,
        price: args.limitPrice,
        currency: args.currencyOut,
        orderType: OrderType.LIMIT,
        timeInForce: args.timeInForce,
        handlingInstruction: HandlingInstruction.AUTOMATED_NO_BROKER,
        // quoteId: quoteId,
        productType: marketProductType,
        leg: [
            CreateFxStrategyLegOrder.fromPartial({
                underlyingCode: args.securityId,
                underlyingSecurityId: args.securityId,
                legValueDate: settlementDate,
                side: args.side,
                qty: args.quantity,
                price: args.limitPrice,
                settlementType: marketSettlementType
            })
        ], // See ts-proto useOptionals
        checkpointTimestamp: [],
        orderMetadata: [
            { key: 'SLIPPAGE', value: slippage } as KeyValue
            // { key: 'QUOTE_ID', value: quoteId } as KeyValue
        ],
        pegPriceType: PegPriceType.LAST_PEG
    });

    const orderSubmissionResponse = await orderServiceClient.unary.createFxOrderRequest(orderRequestLimit, {
        metadata: Metadata({
            'authorization-token': credentials.authToken
        })
    });

    logToServer(
        'info',
        JSON.stringify({
            message: `User [${credentials.username}] has requested a Limit Order for Account [${
                currentAccount || credentials.accounts[0].code
            }].`,
            requestBody: orderRequestLimit,
            responseBody: orderSubmissionResponse
        })
    );

    return orderSubmissionResponse;
}

export async function cancelLimitOrder(args: Partial<CancelOrderRequest>, credentials: User) {
    let cancelOrderSubmissionResponse;
    const orderRequestLimit = CancelOrderRequest.fromPartial({
        clientRequestId: uuidv4(),
        orderId: args.orderId,
        clOrdId: args.clOrdId,
        origClOrdId: args.origClOrdId,
        onBehalfOfCompId: args.onBehalfOfCompId,
        deliverToCompId: args.deliverToCompId,
        checkpointTimestamp: args.checkpointTimestamp,
        onBehalfOfUsername: args.onBehalfOfUsername,
        parentOrderId: args.parentOrderId
    });

    try {
        cancelOrderSubmissionResponse = await orderServiceClient.unary.cancelOrderRequest(orderRequestLimit, {
            metadata: Metadata({
                'authorization-token': credentials.authToken
            })
        });
    } catch (e) {
        console.error('Error cancelling order:', e);
    }

    logToServer(
        'info',
        JSON.stringify({
            message: `User [${credentials.username}] has requested to Cancel a Limit Order for OrderId [${args.orderId}].`,
            requestBody: orderRequestLimit,
            responseBody: cancelOrderSubmissionResponse
        })
    );

    return cancelOrderSubmissionResponse;
}

export async function amendOrder(args: Partial<AmendOrderRequest>, credentials: User) {
    const amendOrderRequest = AmendOrderRequest.fromPartial({
        clientRequestId: uuidv4(),
        orderId: args.orderId,
        clOrdId: args.clOrdId,
        account: args.account,
        qty: args.qty,
        price: args.price,
        stopPrice: args.stopPrice
        // origClOrdId: args.origClOrdId,
        // orderText: args.orderText,
        // giveIn: args.giveIn,
        // giveUp: args.giveUp,
        // onBehalfOfCompId: args.onBehalfOfCompId,
        // deliverToCompId: args.deliverToCompId,
        // quoteId: args.quoteId,
        // checkpointTimestamp: args.checkpointTimestamp,
        // orderMetadata: args.orderMetadata,
        // timeInForceExpiry: args.timeInForceExpiry,
        // executionInstruction: args.executionInstruction,
        // minQty: args.minQty,
        // maxShow: args.maxShow,
        // commission: args.commission,
        // instructionsMessage: args.instructionsMessage
    });

    const amendOrderSubmissionResponse = await orderServiceClient.unary.amendOrderRequest(amendOrderRequest, {
        metadata: Metadata({
            'authorization-token': credentials.authToken
        })
    });

    logToServer(
        'info',
        JSON.stringify({
            message: `User [${credentials.username}] has requested to Amend an Order for OrderId [${args.orderId}].`,
            requestBody: amendOrderRequest,
            responseBody: amendOrderSubmissionResponse
        })
    );

    return amendOrderSubmissionResponse;
}

export async function amendOCOOrder(
    args: {
        listId: string;
        order1: Partial<AmendOrderRequest> & {
            username: string;
        };
        order2: Partial<AmendOrderRequest> & {
            username: string;
        };
    },
    credentials: User
) {
    const amendOrderRequest = AmendStrategyOrderRequest.fromPartial({
        clientRequestId: uuidv4(),
        listID: args.listId,
        contingencyType: ContingencyType.OCO,
        amendOrderRequests: [
            {
                orderId: args.order1.orderId,
                account: args.order1.account,
                orderText: args.order1.orderText,
                qty: args.order1.qty,
                price: args.order1.price,
                stopPrice: args.order1.stopPrice,
                username: args.order1.username
            } as any,
            {
                orderId: args.order2.orderId,
                account: args.order2.account,
                orderText: args.order2.orderText,
                qty: args.order2.qty,
                price: args.order2.price,
                stopPrice: args.order2.stopPrice,
                username: args.order2.username
            } as any
        ]
        // origClOrdId: args.origClOrdId,
        // orderText: args.orderText,
        // giveIn: args.giveIn,
        // giveUp: args.giveUp,
        // onBehalfOfCompId: args.onBehalfOfCompId,
        // deliverToCompId: args.deliverToCompId,
        // quoteId: args.quoteId,
        // checkpointTimestamp: args.checkpointTimestamp,
        // orderMetadata: args.orderMetadata,
        // timeInForceExpiry: args.timeInForceExpiry,
        // executionInstruction: args.executionInstruction,
        // minQty: args.minQty,
        // maxShow: args.maxShow,
        // commission: args.commission,
        // instructionsMessage: args.instructionsMessage
    });

    const amendOrderSubmissionResponse = await orderServiceClient.unary.amendStrategyOrderRequest(amendOrderRequest, {
        metadata: Metadata({
            'authorization-token': credentials.authToken
        })
    });

    logToServer(
        'info',
        JSON.stringify({
            message: `User [${credentials.username}] has requested to Amend an OCO Order for OrderId [${args.order1.orderId}, ${args.order2.orderId}].`,
            requestBody: amendOrderRequest,
            responseBody: amendOrderSubmissionResponse
        })
    );

    return amendOrderSubmissionResponse;
}

interface StopOrderArguments extends MarketOrderArguments {
    limitPrice: number;
    stopPrice: number;
    slippage: string;
    timeInForce: TimeInForceType;
    orderType: OrderType;
}

export async function submitStopOrder(
    args: StopOrderArguments,
    credentials: User,
    settlementDate?: string,
    currentAccount?: string
) {
    const config = getUniversalInstrument(args.securityId);

    const marketExchangeCode = exchangeCode;
    let marketSettlementType = 'SP';
    let marketProductType = ProductType.SPOT;

    if (['Index', 'Commodity'].includes(config?.type) && config?.product_type !== 'FX') {
        marketSettlementType = 'TOM';
        marketProductType = ProductType.CFD;
    }

    const isStopMarket = args.orderType === OrderType.STOP_MARKET;

    // Stop order
    const orderRequestStop = createStopOrderPayload({
        ...args,
        marketExchangeCode,
        marketSettlementType,
        marketProductType,
        valueDate: settlementDate,
        credentials,
        currentAccount,
        isStopMarket
    });

    const orderSubmissionResponse = await orderServiceClient.unary.createFxOrderRequest(orderRequestStop, {
        metadata: Metadata({
            'authorization-token': credentials.authToken
        })
    });

    logToServer(
        'info',
        JSON.stringify({
            message: `User [${credentials.username}] has requested a Stop Order for Account [${
                currentAccount || credentials.accounts[0].code
            }].`,
            requestBody: orderRequestStop,
            responseBody: orderSubmissionResponse
        })
    );

    return orderSubmissionResponse;
}

interface OcoOrderArguments extends MarketOrderArguments {
    order1: {
        stopPrice: number;
        stopLimitPrice: number;
        limitPrice: number;
        type: { label: string; value: OrderType };
        quantity: number;
    };
    order2: {
        stopPrice: number;
        stopLimitPrice: number;
        limitPrice: number;
        type: { label: string; value: OrderType };
        slippage: string;
        quantity: number;
    };
    timeInForce: TimeInForceType;
}

export async function submitOcoOrder(
    args: OcoOrderArguments,
    credentials: User,
    settlementDate?: string,
    currentAccount?: string
) {
    const config = getUniversalInstrument(args.securityId);

    const marketExchangeCode = exchangeCode;
    let marketSettlementType = 'SP';
    let marketProductType = ProductType.SPOT;

    if (['Index', 'Commodity'].includes(config?.type) && config?.product_type !== 'FX') {
        marketSettlementType = 'TOM';
        marketProductType = ProductType.CFD;
    }

    const order2Payload = {
        ...args.order2,
        isStopMarket: args.order2.type.value === OrderType.STOP_MARKET
    };

    const firstOrder = createLimitOrderPayload({
        ...args,
        ...args.order1,
        marketExchangeCode,
        marketSettlementType,
        marketProductType,
        valueDate: settlementDate,
        credentials,
        currentAccount,
        limitPrice: args.order1.limitPrice,
        slippage: '0'
    });

    const secondOrder = createStopOrderPayload({
        ...args,
        ...args.order2,
        marketExchangeCode,
        marketSettlementType,
        marketProductType,
        valueDate: settlementDate,
        credentials,
        currentAccount,
        stopPrice: order2Payload.stopPrice,
        limitPrice: order2Payload.stopLimitPrice,
        slippage: order2Payload.slippage,
        orderType: args.order2.type.value,
        isStopMarket: order2Payload.isStopMarket,
        spotPrice: args.spotPrice
    });

    const orderRequestOco = CreateFxListOrder.fromPartial({
        clientRequestId: uuidv4(),
        listID: `STRATEGYORDER:PARENT:${uuidv4()}`,
        contingencyType: ContingencyType.OCO,
        strategyOrders: [firstOrder, secondOrder]
    });

    const orderSubmissionResponse = await orderServiceClient.unary.createStrategyOrderRequest(orderRequestOco, {
        metadata: Metadata({
            'authorization-token': credentials.authToken
        })
    });

    logToServer(
        'info',
        JSON.stringify({
            message: `User [${credentials.username}] has requested a OCO Order for Account [${
                currentAccount || credentials.accounts[0].code
            }].`,
            requestBody: orderRequestOco,
            responseBody: orderSubmissionResponse
        })
    );

    return orderSubmissionResponse;
}

export async function getBlotterOrders(credentials: User) {
    // Get all orders
    const getAllOrdersRequest = await orderServiceClient.unary.findAllFxOrders(
        { clientRequestId: uuidv4() },
        { metadata: Metadata({ 'authorization-token': credentials.authToken }) }
    );

    const items: BlotterItem[] = [];

    for (const orderUpdate of getAllOrdersRequest.orders) {
        const { fxOrderSnapshotDownstreamEvent, createOrderRequestRejectDownstreamEvent } = orderUpdate;

        if (fxOrderSnapshotDownstreamEvent && !createOrderRequestRejectDownstreamEvent?.rejectReason) {
            items.push(parseBlotterOrder(fxOrderSnapshotDownstreamEvent));
        }
    }

    return items;
}

export async function getLuceraBlotterOrders(luceraJwt: string) {
    // Get all orders
    const items: LuceraBlotterItem[] = [];

    const jwt = luceraJwt;

    const luceraRestURL = window.config?.lucera?.rest;
    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });
    const res = await lumeFxRestClient.getOrders({});
    if (!res.Data) return items;
    for (const order of res.Data) {
        items.push(parseLuceraBlotterOrder(order)); // returns LuceraBlotterItem
    }

    return items;
}

export async function getBlotterTrades(credentials: User, blotterOrders: BlotterItem[]) {
    return await findSnapshotsFromAllOrders({
        historicOrders: blotterOrders,
        credentials,
        client: orderServiceClient.unary
    });
}

export async function searchBlotter(credentials: User, criteria: any, startDate?: Date, endDate?: Date) {
    // Setting citeria to any because it only accepts number, not string as its stated. Else it would just return all without filtering.

    const currentTradingDay = getTradingDayStart();

    const orderRequestMarket = HistoricOrderSearchRequest.fromPartial({
        startDate: (startDate || currentTradingDay).getTime().toString(),
        endDate: (endDate || new Date()).getTime().toString(),
        assetType: 'FX',
        executionType: criteria === 0 ? [] : [criteria]
    });

    const { orders } = await orderServiceClient.unary.findAllOrdersBySearch(orderRequestMarket, {
        metadata: Metadata({ 'authorization-token': credentials.authToken })
    });

    return await findSnapshotsFromAllOrders({ historicOrders: orders, credentials, client: orderServiceClient.unary });
}

interface FindOrderHistoryArguments {
    orderId: string;
}

export async function findOrderHistory(args: FindOrderHistoryArguments, credentials: User) {
    // Setting citeria to any because it only accepts number, not string as its stated. Else it would just return all without filtering.

    const orderRequestMarket = FindAllOrderSnapshotsByOrderId.fromPartial({
        clientRequestId: uuidv4(),
        orderId: args.orderId
    });

    const orderHistories = await orderServiceClient.unary.findAllOrderSnapshotsByOrderId(orderRequestMarket, {
        metadata: Metadata({
            'authorization-token': credentials.authToken
        })
    });

    // console.log({
    //     API: 'findAllOrderSnapshotsByOrderId',
    //     request: orderRequestMarket,
    //     response: orderHistories.orders
    // });

    const items: OrderHistory[] = [];

    for (const orderEvent of orderHistories.orders) {
        const { fxOrderSnapshotDownstreamEvent } = orderEvent;
        if (fxOrderSnapshotDownstreamEvent) items.push(parseOrderHistory(fxOrderSnapshotDownstreamEvent));
    }

    return items;
}

interface CreateLimitOrderPayloadArgs {
    securityId: string;
    side: Side;
    quantity: number;
    currencyOut: string;
    timeInForce: TimeInForceType;
    marketExchangeCode: string;
    marketSettlementType: string;
    marketProductType: ProductType;
    valueDate?: string;
    limitPrice: number;
    slippage: string;
    credentials: User;
    currentAccount?: string;
}

const createLimitOrderPayload = (args: CreateLimitOrderPayloadArgs) => {
    const slippage = args.slippage === 'NaN' ? '0' : args.slippage;
    return CreateFxOrderRequest.fromPartial({
        clientRequestId: uuidv4(),
        clientOrderId: uuidv4(),
        exchangeIdSource: 'MIC',
        exchange: args.marketExchangeCode,
        securityIdSource: 'EXCHANGE',
        securityCode: args.securityId,
        securityId: args.securityId,
        accountType: AccountType.CLIENT,
        account: args.currentAccount || args.credentials.accounts[0].code,
        side: args.side,
        qty: args.quantity,
        price: args.limitPrice,
        currency: args.currencyOut,
        orderType: OrderType.LIMIT,
        timeInForce: args.timeInForce,
        handlingInstruction: HandlingInstruction.AUTOMATED_NO_BROKER,
        // quoteId: quoteId,
        productType: args.marketProductType,
        leg: [
            CreateFxStrategyLegOrder.fromPartial({
                underlyingCode: args.securityId,
                underlyingSecurityId: args.securityId,
                legValueDate: args.valueDate,
                side: args.side,
                qty: args.quantity,
                price: args.limitPrice,
                settlementType: args.marketSettlementType
            })
        ], // See ts-proto useOptionals
        checkpointTimestamp: [],
        orderMetadata: [
            { key: 'SLIPPAGE', value: slippage } as KeyValue
            // { key: 'QUOTE_ID', value: quoteId } as KeyValue
        ],
        pegPriceType: PegPriceType.LAST_PEG
    });
};

interface CreateStopOrderPayloadArgs {
    securityId: string;
    side: Side;
    quantity: number;
    currencyOut: string;
    timeInForce: TimeInForceType;
    marketExchangeCode: string;
    marketSettlementType: string;
    marketProductType: ProductType;
    valueDate?: string;
    stopPrice: number;
    limitPrice: number;
    slippage: string;
    orderType: OrderType;
    isStopMarket: boolean;
    spotPrice?: number;
    credentials: User;
    currentAccount?: string;
}

const createStopOrderPayload = (args: CreateStopOrderPayloadArgs) => {
    const slippage = args.slippage === 'NaN' ? '0' : args.slippage;

    return CreateFxOrderRequest.fromPartial({
        clientRequestId: uuidv4(),
        clientOrderId: uuidv4(),
        exchangeIdSource: 'MIC',
        exchange: args.marketExchangeCode,
        securityIdSource: 'EXCHANGE',
        securityCode: args.securityId,
        securityId: args.securityId,
        accountType: AccountType.CLIENT,
        account: args.currentAccount || args.credentials.accounts[0].code,
        side: args.side,
        qty: args.quantity,
        price: args.isStopMarket ? args.spotPrice : args.limitPrice,
        stopPrice: args.stopPrice,
        currency: args.currencyOut,
        orderType: args.orderType,
        timeInForce: args.timeInForce,
        handlingInstruction: HandlingInstruction.AUTOMATED_NO_BROKER,
        // quoteId: quoteId,
        productType: args.marketProductType,
        leg: [
            CreateFxStrategyLegOrder.fromPartial({
                legForwardPts: 0,
                legValueDate: args.valueDate,
                qty: args.quantity,
                settlementType: args.marketSettlementType,
                side: args.side,
                price: args.isStopMarket ? args.spotPrice : args.limitPrice,
                spotPrice: args.isStopMarket ? args.spotPrice : undefined,
                underlyingCode: args.securityId,
                underlyingSecurityId: args.securityId
            })
        ], // See ts-proto useOptionals
        checkpointTimestamp: [],
        orderMetadata: [{ key: 'SLIPPAGE', value: args.isStopMarket ? '0' : slippage } as KeyValue],
        pegPriceType: PegPriceType.LAST_PEG
    });
};

// CreateFxOrderRequest.fromPartial({
//     clientOrderId: uuidv4(),
//     exchangeIdSource: 'MIC',
//     exchange: marketExchangeCode,
//     securityIdSource: 'EXCHANGE',
//     securityCode: args.securityId,
//     securityId: args.securityId,
//     accountType: AccountType.CLIENT,
//     account: currentAccount || credentials.accounts[0].code,
//     side: args.side,
//     qty: args.quantity,
//     price: order1Payload.isStopMarket ? args.spotPrice : args.limitPrice1,
//     stopPrice: order1Payload.stopPrice,
//     currency: args.currencyOut,
//     orderType: order1Payload.orderType,
//     timeInForce: args.timeInForce,
//     handlingInstruction: HandlingInstruction.AUTOMATED_NO_BROKER,
//     // quoteId: quoteId,
//     productType: marketProductType,
//     leg: [
//         CreateFxStrategyLegOrder.fromPartial({
//             legForwardPts: 0,
//             legValueDate: valueDate,
//             qty: args.quantity,
//             settlementType: marketSettlementType,
//             side: args.side,
//             price: order1Payload.isStopMarket ? args.spotPrice : args.limitPrice1,
//             spotPrice: order1Payload.isStopMarket ? args.spotPrice : undefined,
//             underlyingCode: args.securityId,
//             underlyingSecurityId: args.securityId
//         })
//     ], // See ts-proto useOptionals
//     checkpointTimestamp: [],
//     orderMetadata: [
//         { key: 'SLIPPAGE', value: order1Payload.isStopMarket ? '0' : args.slippage1 } as KeyValue
//     ],
//     pegPriceType: PegPriceType.LAST_PEG
//     // "bookAsFilled": false,
//     // "clientRequestId": "d68c43ae-3e2d-41e4-adfc-2b9fa6f75a23",
//     // "maxShow": 0,
//     // "orderType": 2,
//     // "orderPartiesGroup": [
//     //   {
//     //     "partyId": "ext-dnh@gc.exchange",
//     //     "partyIdSource": 6,
//     //     "partyRole": 20
//     //   }
//     // ],
// })

// const rejectOrderIfAppleDevice = (callback, action = 'order') => {
//     if (!checkiOSDevice()) {
//         callback();
//     } else {
//         const state = store.getState();
//         const isOrder = action === 'order';
//         const isCancel = action === 'cancel';
//         const isAmend = action === 'amend';

//         toastError({
//             body: (
//                 <div>
//                     <p>
//                         {isOrder &&
//                             'Placing orders on an Apple device is temporarily unavailable, please reach out to support.'}
//                         {isCancel &&
//                             'Cancelling orders on an Apple device is temporarily unavailable, please reach out to support.'}
//                         {isAmend &&
//                             'Amending orders on an Apple device is temporarily unavailable, please reach out to support.'}
//                     </p>
//                 </div>
//             ),
//             title: `${isOrder ? 'Order' : 'Action'} Rejected`,
//             persist: true,
//             toastConfig: parseNotificationSettings(state.settings.notifications)
//         });
//     }
// };
